import type { WebChatConfig } from "@chatbotgang/web-sdk-core/model";
import { css } from "@emotion/react";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import z from "zod";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "@/components/Accordion";
import { Typography } from "@/components/Typography";

const cssTriggers = {
  sectionTitle: css`
    font-weight: 500;
  `,
  subsectionTitle: css`
    font-weight: 500;
  `,
} as const;

const DisplayFilterTypeEnum = z.enum([
  "all",
  "match",
  "excludeMatch",
  "include",
  "exclude",
]);
type DisplayFilterType = z.infer<typeof DisplayFilterTypeEnum>;

const displayFilters = [
  {
    type: DisplayFilterTypeEnum.enum.match,
    labelKey: "webChatConfig.triggers.whenUrlPathIs",
    placeholderKey: "webChatConfig.triggers.whenUrlPathIs.placeholder",
  },
  {
    type: DisplayFilterTypeEnum.enum.excludeMatch,
    labelKey: "webChatConfig.triggers.whenUrlPathIsNot",
    placeholderKey: "webChatConfig.triggers.whenUrlPathIsNot.placeholder",
  },
  {
    type: DisplayFilterTypeEnum.enum.include,
    labelKey: "webChatConfig.triggers.whenUrlContains",
    placeholderKey: "webChatConfig.triggers.whenUrlContains.placeholder",
  },
  {
    type: DisplayFilterTypeEnum.enum.exclude,
    labelKey: "webChatConfig.triggers.whenUrlDoesNotContain",
    placeholderKey: "webChatConfig.triggers.whenUrlDoesNotContain.placeholder",
  },
] satisfies Array<{
  type: DisplayFilterType;
  labelKey: string;
  placeholderKey: string;
}>;

export const TriggersForm: React.FC = function TriggersForm() {
  const { t } = useTranslation();
  const { control, watch } = useFormContext<WebChatConfig>();
  const displayFilterType = watch("webChatTrigger.displayFilter.type");
  const currentStrings = watch("webChatTrigger.displayFilter.strings");
  const memoStrings = useRef<Array<string> | undefined>(currentStrings);

  return (
    <Accordion>
      <AccordionSummary>
        <Typography variant="h3" css={cssTriggers.sectionTitle}>
          {t("webChatConfig.triggers.title")}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack direction="column" gap="8px">
          <Typography variant="h3" css={cssTriggers.subsectionTitle}>
            {t("webChatConfig.triggers.whereToDisplay")}
          </Typography>
          <Controller
            name="webChatTrigger.displayFilter"
            control={control}
            render={({ field }) => (
              <RadioGroup
                value={field.value?.type || DisplayFilterTypeEnum.enum.all}
                onChange={(e) => {
                  const parseResult = DisplayFilterTypeEnum.safeParse(
                    e.target.value,
                  );
                  if (!parseResult.success) {
                    return;
                  }
                  const newType = parseResult.data;

                  if (currentStrings !== undefined)
                    memoStrings.current = currentStrings;

                  if (newType === DisplayFilterTypeEnum.enum.all) {
                    field.onChange({ type: DisplayFilterTypeEnum.enum.all });
                  } else {
                    field.onChange({
                      type: newType,
                      strings: currentStrings || memoStrings.current || [""],
                    });
                  }
                }}
              >
                <Stack gap="8px">
                  <FormControlLabel
                    value={DisplayFilterTypeEnum.enum.all}
                    control={<Radio />}
                    label={
                      <Typography>
                        {t("webChatConfig.triggers.onAllPages")}
                      </Typography>
                    }
                  />
                  {displayFilters.map((option) => (
                    <Stack direction="column" gap="8px" key={option.type}>
                      <FormControlLabel
                        value={option.type}
                        control={<Radio />}
                        label={<Typography>{t(option.labelKey)}</Typography>}
                      />
                      {displayFilterType === option.type && (
                        <Controller
                          name="webChatTrigger.displayFilter.strings.0"
                          control={control}
                          render={({ field, fieldState }) => (
                            <TextField
                              {...field}
                              placeholder={t(option.placeholderKey)}
                              fullWidth
                              autoFocus
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                            />
                          )}
                        />
                      )}
                    </Stack>
                  ))}
                </Stack>
              </RadioGroup>
            )}
          />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
